import { useEffect, useState } from 'react';
import { Button, Card, Col, Form, Row, Tab, Tabs } from 'react-bootstrap';
import PageHeader from 'shared/page-header';
import Layout from 'wrappers/layout';
import PhysicalOrders from './components/PhysicalOrders';
import DeliveryOrdersServices from './services/delivery.order.service';
import { Pagination } from 'shared/common/pagination';
import { userStore } from 'store/UserStore';
import { saveAs } from 'file-saver';
import { toast } from 'react-toastify';
import Loader from 'shared/common/loader';
import './styles/verifyBarCode.css';
 
interface DeliveryOrderFilters {
  metalCode?: string;
  fromDate?: string;
  toDate?: string;
}
 
interface PropsType {}
 
const DeliveryOrders: React.FC<PropsType> = () => {
  const [selectedTab, setSelectedTab] = useState<string>('deliveryOrders');
  const [deliveryOrders, setDeliveryOrders] = useState<any[]>([]);
  const [loadingOrders, setLoadingOrders] = useState<boolean>(false);
  const [downloadingInvoice, setDownloadingInvoice] = useState<boolean>(false);

  const [barNumber, setBarNumber] = useState("");
  const [loading, setLoading] = useState(false);
  const [verified, setVerified] = useState(false);
  const [barDetails, setBarDetails] = useState(null);
  const [verificationFailed, setVerificationFailed] = useState(false);


  const [metaData, setMetaData] = useState<{ current: number; pages: number }>({
    current: 0,
    pages: 0,
  });
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [filter, setFilter] = useState<DeliveryOrderFilters>({
    metalCode: '',
    fromDate: '',
    toDate: '',
  });
 
  const [user] = userStore((state: any) => [state.user]);
 
  // Tab change
  const onChangeTab = (tab: string | null) => {
    if (tab) setSelectedTab(tab);
  };
 
  // Filter change
  const handleChangeFilter = (e: React.ChangeEvent<any>) => {
    const { name, value } = e.target;
    setFilter((prev) => ({ ...prev, [name]: value }));
  };
 
  const getDeliveryOrders = async (
    page: number = currentPage,
    filtersParam: DeliveryOrderFilters = filter
  ) => {
    setLoadingOrders(true);
    try {
      const res = await DeliveryOrdersServices.getDeliverOrders(
        filtersParam,
        page
      );
 
      setCurrentPage(page);
      setMetaData({
        current: res.data.meta.page,
        pages: res.data.meta.totalPages + 1,
      });
 
      setDeliveryOrders(res?.data?.data?.result || []);
    } catch (error) {
      console.error('Failed to fetch delivery orders:', error);
      setDeliveryOrders([]);
    } finally {
      setLoadingOrders(false);
    }
  };
 
  const applyFilters = () => {
    setCurrentPage(currentPage);
    getDeliveryOrders(currentPage);
  };
 
  const resetFilters = () => {
    const clearedFilters = { metalCode: '', fromDate: '', toDate: '' };
    setFilter(clearedFilters);
    setCurrentPage(0);
    getDeliveryOrders(0, clearedFilters);
  };
 
  const downloadDeliveryInvoice = async (gstInvoiceRef: any) => {
    if (!gstInvoiceRef) {
      toast.error('Invoice reference not found');
      return;
    }
 
    setDownloadingInvoice(true);
    try {
      const res = await DeliveryOrdersServices.downloadDeliveryInvoice(
        gstInvoiceRef,
        user?.userId
      );
 
      if (res.status === 200 && res.data?.data?.fileUrl) {
        const fileUrl = res.data.data.fileUrl;
        window.open(fileUrl, '_blank');
        
      } else {
        toast.error('Failed to download invoice');
      }
    } catch (error: any) {
      console.error('Download invoice error:', error);
      if (error?.response?.data?.errors?.length > 0) {
        toast.error(error.response.data.errors[0].title);
      } else {
        toast.error('Something went wrong while downloading invoice');
      }
    } finally {
      setDownloadingInvoice(false);
    }
  };

   const handleVerify = async () => {
    // Reset all flags before verification
    setVerified(false);
    setVerificationFailed(false);

    // Handle empty input first
    if (barNumber.trim().length === 0) {
      setVerificationFailed(true);
      // Optionally set a local error message
      setBarDetails(null);
      return;
    }

    setLoading(true);

    try {
      const response = await DeliveryOrdersServices.verifyBar(barNumber.trim());

      if (response?.data?.data?.isCertificateValid) {
        setBarDetails(response.data.data);
        setVerified(true);
        setVerificationFailed(false);
      } else {
        setVerificationFailed(true);
        setBarDetails(null);
      }
    } catch (err) {
      console.error("Verification error:", err);
      setVerificationFailed(true);
      setBarDetails(null);
    } finally {
      setLoading(false);
    }
  };

 
  useEffect(() => {
    document.title = 'Delivery Orders - eBullion';
    getDeliveryOrders(0);
  }, []);
 
  return (
    <Layout>
      {downloadingInvoice && <Loader/>}
      <div className='pb'>
        <PageHeader
          mainTitle='Delivery Orders'
          subTitle='View and track all your physical delivery orders'
          isShowSub={true}
        />
 
        <Row>
          <Col md={12}>
            <Tabs
              activeKey={selectedTab}
              id='delivery-orders-tab'
              className='mb-3'
              onSelect={onChangeTab}
            >
              <Tab eventKey='deliveryOrders' title='Delivery Orders'>
                <div>
                  <h6>Order History</h6>
 
                  <Card>
                    <Card.Body>
                      <Row className='align-items-center'>
                        <Col md={3}>
                          <Form.Group className='mb-3' controlId='orderMetal'>
                            <Form.Label>Order Metal</Form.Label>
                            <Form.Select
                              name='metalCode'
                              onChange={handleChangeFilter}
                              value={filter.metalCode}
                            >
                              <option value=''>All metals</option>
                              <option value='Ag'>Silver (Ag)</option>
                              <option value='Au'>Gold (Au)</option>
                            </Form.Select>
                          </Form.Group>
                        </Col>
 
                        <Col md={2}>
                          <Form.Group
                            className='mb-3'
                            controlId='orderFromDate'
                          >
                            <Form.Label>Order From Date</Form.Label>
                            <Form.Control
                              type='date'
                              name='fromDate'
                              onChange={handleChangeFilter}
                              value={filter.fromDate}
                            />
                          </Form.Group>
                        </Col>
 
                        <Col md={2}>
                          <Form.Group className='mb-3' controlId='orderToDate'>
                            <Form.Label>Order To Date</Form.Label>
                            <Form.Control
                              type='date'
                              name='toDate'
                              onChange={handleChangeFilter}
                              value={filter.toDate}
                              min={filter.fromDate}
                              disabled={!filter.fromDate}
                            />
                          </Form.Group>
                        </Col>
 
                        <Col md={2} className='p-0 pt-2'>
                          <Button
                            className='mx-2'
                            variant='primary'
                            onClick={applyFilters}
                            disabled={loadingOrders}
                          >
                            {loadingOrders ? 'Loading...' : 'Apply'}
                          </Button>
                          <Button
                            variant='secondary'
                            onClick={resetFilters}
                            disabled={loadingOrders}
                          >
                            Reset
                          </Button>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
 
                  <div className='t-wrap overflow-auto mt-3'>
                    <PhysicalOrders
                      orders={deliveryOrders}
                      downloadDeliveryInvoice={downloadDeliveryInvoice}
                    />
                  </div>
 
                  {/* Pagination */}
                  <Row>
                    <Col md={12}>
                      <Pagination
                        meta={metaData}
                        handlePageChange={(page: number) =>
                          getDeliveryOrders(page)
                        }
                      />
                    </Col>
                  </Row>
                </div>
              </Tab>
              <Tab eventKey='verifyOrders' title='Verify Your Purchase'>
                <div className="verify-bar-code-container">
                  <div className="verify-bar-code-subcontainer">

                    {/* Verification Box - Always visible */}
                    <div className="verification-box">
                      <h6>Enter Certificate Number</h6>
                      <input
                        type="text"
                        placeholder="Enter Certificate Number"
                        value={barNumber}
                        onChange={(e) => {
                          const value = e.target.value.toUpperCase();
                          setBarNumber(value);

                          // Reset states if user modifies input
                          if (verified || verificationFailed) {
                            setVerified(false);
                            setVerificationFailed(false);
                            setBarDetails(null);
                          }
                        }}
                        disabled={loading}
                      />

                      <button
                        className="verify-btn"
                        onClick={handleVerify}
                        disabled={loading || barNumber.trim().length === 0}
                      >
                        {loading ? "Verifying..." : "Verify Authenticity"}
                      </button>

                      {verificationFailed && barNumber.trim().length === 0 && (
                        <p className="error-text">Please enter a valid Certicard Number.</p>
                      )}
                    </div>


                    {/* Success Box */}
                    {verified && barDetails && (
                      <div className="result-box success">
                        <h2 className="verified-title success">

                          <svg width="26px" height="26px" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><g id="SVGRepo_bgCarrier" stroke-width="0"></g><g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g><g id="SVGRepo_iconCarrier"> <path fill-rule="evenodd" clip-rule="evenodd" d="M9.5924 3.20027C9.34888 3.4078 9.22711 3.51158 9.09706 3.59874C8.79896 3.79854 8.46417 3.93721 8.1121 4.00672C7.95851 4.03705 7.79903 4.04977 7.48008 4.07522C6.6787 4.13918 6.278 4.17115 5.94371 4.28923C5.17051 4.56233 4.56233 5.17051 4.28923 5.94371C4.17115 6.278 4.13918 6.6787 4.07522 7.48008C4.04977 7.79903 4.03705 7.95851 4.00672 8.1121C3.93721 8.46417 3.79854 8.79896 3.59874 9.09706C3.51158 9.22711 3.40781 9.34887 3.20027 9.5924C2.67883 10.2043 2.4181 10.5102 2.26522 10.8301C1.91159 11.57 1.91159 12.43 2.26522 13.1699C2.41811 13.4898 2.67883 13.7957 3.20027 14.4076C3.40778 14.6511 3.51158 14.7729 3.59874 14.9029C3.79854 15.201 3.93721 15.5358 4.00672 15.8879C4.03705 16.0415 4.04977 16.201 4.07522 16.5199C4.13918 17.3213 4.17115 17.722 4.28923 18.0563C4.56233 18.8295 5.17051 19.4377 5.94371 19.7108C6.278 19.8288 6.6787 19.8608 7.48008 19.9248C7.79903 19.9502 7.95851 19.963 8.1121 19.9933C8.46417 20.0628 8.79896 20.2015 9.09706 20.4013C9.22711 20.4884 9.34887 20.5922 9.5924 20.7997C10.2043 21.3212 10.5102 21.5819 10.8301 21.7348C11.57 22.0884 12.43 22.0884 13.1699 21.7348C13.4898 21.5819 13.7957 21.3212 14.4076 20.7997C14.6511 20.5922 14.7729 20.4884 14.9029 20.4013C15.201 20.2015 15.5358 20.0628 15.8879 19.9933C16.0415 19.963 16.201 19.9502 16.5199 19.9248C17.3213 19.8608 17.722 19.8288 18.0563 19.7108C18.8295 19.4377 19.4377 18.8295 19.7108 18.0563C19.8288 17.722 19.8608 17.3213 19.9248 16.5199C19.9502 16.201 19.963 16.0415 19.9933 15.8879C20.0628 15.5358 20.2015 15.201 20.4013 14.9029C20.4884 14.7729 20.5922 14.6511 20.7997 14.4076C21.3212 13.7957 21.5819 13.4898 21.7348 13.1699C22.0884 12.43 22.0884 11.57 21.7348 10.8301C21.5819 10.5102 21.3212 10.2043 20.7997 9.5924C20.5922 9.34887 20.4884 9.22711 20.4013 9.09706C20.2015 8.79896 20.0628 8.46417 19.9933 8.1121C19.963 7.95851 19.9502 7.79903 19.9248 7.48008C19.8608 6.6787 19.8288 6.278 19.7108 5.94371C19.4377 5.17051 18.8295 4.56233 18.0563 4.28923C17.722 4.17115 17.3213 4.13918 16.5199 4.07522C16.201 4.04977 16.0415 4.03705 15.8879 4.00672C15.5358 3.93721 15.201 3.79854 14.9029 3.59874C14.7729 3.51158 14.6511 3.40781 14.4076 3.20027C13.7957 2.67883 13.4898 2.41811 13.1699 2.26522C12.43 1.91159 11.57 1.91159 10.8301 2.26522C10.5102 2.4181 10.2043 2.67883 9.5924 3.20027ZM16.3735 9.86314C16.6913 9.5453 16.6913 9.03 16.3735 8.71216C16.0557 8.39433 15.5403 8.39433 15.2225 8.71216L10.3723 13.5624L8.77746 11.9676C8.45963 11.6498 7.94432 11.6498 7.62649 11.9676C7.30866 12.2854 7.30866 12.8007 7.62649 13.1186L9.79678 15.2889C10.1146 15.6067 10.6299 15.6067 10.9478 15.2889L16.3735 9.86314Z" fill="#301250"></path> </g></svg>

                          {barDetails.metalCode === "Ag" ? "Silver" : "Gold"} Bar Verified
                        </h2>
                        <p className="verified-text">
                          Your eBullion {barDetails.metalCode === "Ag" ? "Silver" : "Gold"} bar is 100% genuine. This certicard number matches our official records, ensuring the purity and trust you invested in.
                        </p>
                        <div className="details-grid">
                          <div className="detail-item"><strong>Certificate No:</strong> {barDetails.certificateNo}</div>
                          <div className="detail-item"><strong>Weight:</strong> {barDetails.weight} g</div>
                          <div className="detail-item"><strong>Fineness:</strong> {barDetails.fineness}</div>
                          <div className="detail-item"><strong>Metal Code:</strong> {barDetails.metalCode}</div>
                          <div className="detail-item"><strong>Order Date:</strong> {barDetails.orderDate}</div>
                        </div>
                        <img src={barDetails.image} alt={`Bar ${barDetails.certificateNo}`} className="product-img" />
                      </div>
                    )}

                    {/* Failure Box */}
                    {verificationFailed && (
                      <div className="result-box failure">
                        <h2 className="verified-title failure">Verification Failed</h2>
                        <p className="verified-text">
                          The certicard number entered does not match our official records.<br />
                          Please recheck the number and try again.<br />
                          If the issue continues, contact eBullion Support at <a href="mailto:support@ebullion.in">support@ebullion.in</a>
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </Tab>
            </Tabs>
          </Col>
        </Row>
      </div>
    </Layout>
  );
};
 
export default DeliveryOrders;